/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Button, IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import * as MsDyn365 from '@msdyn365-commerce/core';
import classnames from 'classnames';
import * as React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { ICittaBlocFormData } from './citta-bloc-form.data';
import { ICittaBlocFormProps } from './citta-bloc-form.props.autogenerated';

interface ICittaBlocFormFormState {
    firstName: string;
    lastName: string;
    emailAddress: string;
    phoneNumber: string;
    storeName: string;
    eAValError: boolean;
    eAError: boolean;
    fNError: boolean;
    lNError: boolean;
    pNError: boolean;
    sNError: boolean;
    rCCError: boolean;
    reqError: boolean;
    responseReceived: boolean;
    responseError: boolean;
    responseSuccess: boolean;
    showResponseSuccess: boolean;
    reCaptchaChecked: boolean;

    firstNameErrorClass: string;
    lastNameErrorClass: string;
    emailErrorClass: string;
    storeNameErrorClass: string;
    phoneNameErrorClass: string;
}

export interface ICittaBlocFormForm {
    FormWrapper: INodeProps;
    FormContainer: INodeProps;
    firstAndLastNameInput: React.ReactNode;
    emailInput: React.ReactNode;
    phoneInput: React.ReactNode;
    storeInput: React.ReactNode;
    reCaptcha: React.ReactNode;
    subscribeButtton: React.ReactNode;
    subscribeThanks: React.ReactNode;
}


export interface ICittaBlocFormViewProps extends ICittaBlocFormProps<ICittaBlocFormData> {
    SubscribeWrapper: IModuleProps;
    subscribeResponse: React.ReactNode;
    subscribeForm: ICittaBlocFormForm;
    responseSuccess: boolean;
    showResponseSuccess: boolean;
    formErrors: React.ReactNode;
    formErrorsNodeProps: INodeProps;
    successResponse: React.ReactNode;
    showSuccessResponse: boolean;
    successResponseNodeProps: INodeProps;
}

/**
 *
 * CittaBlocForm component
 * @extends {React.PureComponent<ICittaBlocFormProps<ICittaBlocFormData>>}
 */
class CittaBlocForm extends React.PureComponent<ICittaBlocFormProps<ICittaBlocFormData>, ICittaBlocFormFormState> {
    private _FirstNameInputRef: React.RefObject<HTMLInputElement>;
    private _LastNameInputRef: React.RefObject<HTMLInputElement>;
    private _EmailAddressInputRef: React.RefObject<HTMLInputElement>;
    private _StoreInputRef: React.RefObject<HTMLSelectElement>;
    private _recaptchaRef: React.RefObject<ReCAPTCHA>;
    private _PhoneInputRef: React.RefObject<HTMLInputElement>;

    private firstNameValue: boolean = false;
    private lastNameValue: boolean = false;
    private emailAddressValue: boolean = false;
    private phoneNumberValue: boolean = false;
    private storeNameValue: boolean = false;
    private formInputValues: boolean = false;
    private responseMessage: string | undefined;
    private moduleClassName: string = 'newsletterSubWrap';
    private recaptchaErrorMessage: string | undefined;

    constructor(props: ICittaBlocFormProps<ICittaBlocFormData>) {
        super(props);
        this._FirstNameInputRef = React.createRef();
        this._LastNameInputRef = React.createRef();
        this._EmailAddressInputRef = React.createRef();
        this._StoreInputRef = React.createRef();
        this._PhoneInputRef = React.createRef();
        this._recaptchaRef = React.createRef();
        this._handleSubmit = this._handleSubmit.bind(this);

        this.state = {
            firstName: '',
            lastName: '',
            emailAddress: '',
            phoneNumber: '',
            storeName: '',
            eAValError: false,
            fNError: false,
            lNError: false,
            pNError: false,
            sNError: false,
            eAError: false,
            rCCError: false,
            reqError: false,
            responseReceived: false,
            responseError: false,
            reCaptchaChecked: false,
            responseSuccess: false,
            showResponseSuccess: false,
            firstNameErrorClass: 'hide',
            lastNameErrorClass: 'hide',
            emailErrorClass: 'hide',
            storeNameErrorClass: 'hide',
            phoneNameErrorClass: 'hide'
        };
    }

    public render(): JSX.Element | null {
        const viewProps: ICittaBlocFormViewProps = {
            ...(this.props as ICittaBlocFormProps<ICittaBlocFormData>),
            SubscribeWrapper: {
                moduleProps: this.props,
                className: classnames(this.moduleClassName, this.props.config.className)
            },
            subscribeResponse: this._getSubmitResponse(),
            subscribeForm: this._renderForm(),
            responseSuccess: this.state.responseSuccess,
            showResponseSuccess: this.state.showResponseSuccess,
            formErrors: this._renderErrors(),
            formErrorsNodeProps: {
                className: 'competition-form-errors'
            },
            successResponse: this._renderSuccessResponse(),
            showSuccessResponse: this.state.responseSuccess,
            successResponseNodeProps: {
                className: 'competition-form-success'
            }
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _getSubmitResponse(): React.ReactNode {
        return (
            this.state.responseReceived && this.responseMessage !== '' || undefined
                ? (
                    <div className={`response-message ${this.state.responseError ? 'response-error' : ''}`}>
                        <p className='response-message-content'>{this.responseMessage}</p>
                    </div>
                )
                : null
        );
    }
    
    private _renderSuccessResponse(): React.ReactNode {
        return (
            this.state.responseSuccess
                ? (
                    <div className="response-succcess">
                        <h2>{this.props.config.successTextResponse}</h2>
                    </div>
                )
                : null
        );
    }

    private _renderForm(): ICittaBlocFormForm {
        return {
            FormWrapper: {
                className: 'competition-form',
            },
            FormContainer: {
                className: 'competition-form-div',
            },
            firstAndLastNameInput: this._renderFirstAndLastNameInput(),
            emailInput: this._renderEmailInput(),
            phoneInput: this._renderPhoneInput(),
            storeInput: this._renderStoreInput(),
            reCaptcha: this._renderReCaptcha(),
            subscribeButtton: this._renderSubmitButton(),
            subscribeThanks: this._renderSubmitThanks()
        };
    }

    private _renderFirstAndLastNameInput(): React.ReactNode {
        return (
            <div className='name-input-container'>
                <div className='name-input'>
                    <input
                        ref={this._FirstNameInputRef}
                        type='text'
                        onChange={this._handleChange('firstName')}
                        aria-label='First Name'
                        className='first-name-input-text'
                        placeholder={this.props.config.firstNamePlaceholderText}
                        value={this.state.firstName}
                        required
                    />
                </div>
                <div className='name-input'>
                    <input
                        ref={this._LastNameInputRef}
                        type='text'
                        onChange={this._handleChange('lastName')}
                        aria-label='First Name'
                        className='name-input-text'
                        placeholder={this.props.config.lastNamePlaceholderText}
                        value={this.state.lastName}
                        required
                    />
                </div>
            </div>
        );
    }

    private _renderPhoneInput(): React.ReactNode {
        return (
            <div className='phone-input'>
                <input
                    ref={this._PhoneInputRef}
                    type='text'
                    onChange={this._handleChange('phoneNumber')}
                    aria-label='Phone Number'
                    className='phone-number-input-text'
                    value={this.state.phoneNumber}
                    required
                    placeholder={this.props.config.phoneNumberPlaceholder}
                />
            </div>
        );
    }

    private _renderStoreInput(): React.ReactNode {
        return (
            <div className='store-input'>
                <select name="storeName" id="storeName"  ref={this._StoreInputRef}
                    onChange={this._handleChange('storeName')}
                >
                    <option>Select Store</option>
                    <option value="Esse">Esse</option>
                    <option value="Hej Hej">Hej Hej</option>
                    <option value="A C Homestore">A C Homestore</option>
                    <option value="BoConcept">BoConcept</option>
                    <option value="Citta">Citta</option>
                    <option value="Father Rabbit">Father Rabbit</option>
                    <option value="Citta Essentials">Citta Essentials</option>
                </select>
            </div>
        );
    }
    
    private _renderEmailInput(): React.ReactNode {
        return (
            <div className='email-address-input'>
                {this.state.eAError || this.state.eAValError ? this._renderAlert('email-address-input') : ''}
                <input
                    ref={this._EmailAddressInputRef}
                    type='email'
                    onChange={this._handleChange('emailAddress')}
                    aria-label='Email Address'
                    className='email-address-input-text'
                    value={this.state.emailAddress}
                    required
                    placeholder={this.props.config.emailInputPlaceholder}
                />
            </div>
        );
    }

    private _renderAlert(className: string): JSX.Element {
        return <></>;
    }

    private _renderRecaptchaAlert(className: string): JSX.Element {

        if (this.props.config.reCaptchaOption) {
            if (!this.state.reCaptchaChecked) {
                this.recaptchaErrorMessage = this.props.resources.recaptchaErrorMessage;
            }
        }

        const rCCError = this.state.rCCError;
        return (
            <>
                {rCCError && (
                    <span className={`${className}__alert`} role='alert' aria-live='assertive'>
                        {
                            this.recaptchaErrorMessage &&
                            <>
                                <span className={`${className}__alert-icon`} aria-hidden='true' />
                                {this.recaptchaErrorMessage}
                            </>
                        }
                    </span>
                )}
            </>
        );
    }

    private _renderReCaptcha(): React.ReactNode {
        return (
            <div>
                <div className=''>
                    {this.state.rCCError ? this._renderRecaptchaAlert('recaptcha-error') : ''}
                    <ReCAPTCHA
                        ref={this._recaptchaRef}
                        sitekey={this.props.config.reCaptchaSiteKey!}
                        size="invisible"
                        onChange={() => this._submit()}
                    />
                </div>
            </div>
        );
    }

    private _renderSubmitButton(): React.ReactNode {
        return (
            <div className="subscribe-button-container">
                <Button
                    className={`subscribe-button btn-thirdary`}
                    title={this.props.config.submitButtonLabel}
                    color='primary'
                    onClick={this._handleSubmit}
                >{this.props.config.submitButtonLabel}
                </Button>
            </div>
        );
    }

    private _renderSubmitThanks(): React.ReactNode {
        return (
            <div>
                <div className={`subscribe-thanks`}>
                    Thanks!
                </div>
            </div>
        );
    }

    private _showErrors = (): void => {
        this.setState({
            emailErrorClass: this._checkEmailError() ? 'show' : 'hide',
            firstNameErrorClass: (this._FirstNameInputRef.current && this._FirstNameInputRef.current.value.length === 0) ? 'show' : 'hide',
            lastNameErrorClass: (this._LastNameInputRef.current && this._LastNameInputRef.current.value.length === 0) ? 'show' : 'hide',
            storeNameErrorClass: (this._StoreInputRef.current && this._StoreInputRef.current.value.length === 0) ? 'show' : 'hide',
            phoneNameErrorClass: (this._PhoneInputRef.current && this._PhoneInputRef.current.value.length === 0) ? 'show' : 'hide',

        });
    }

    private _checkEmailError = (): boolean => {
        const email: string = this._EmailAddressInputRef.current && this._EmailAddressInputRef.current.value || '';
        if (email.length === 0) {
            return true;
        } else {
            return !this._isEmailValid(email);
        }
    }

    private _renderErrors = (): JSX.Element => {
        const {resources} = this.props;
        return (
            <>
                <div className={this.state.firstNameErrorClass}>
                    {resources.firstNameError}
                </div>
                <div className={this.state.lastNameErrorClass}>
                    {resources.lastNameError}
                </div>
                <div className={this.state.emailErrorClass}>
                    {resources.emailError}
                </div>
                <div className={this.state.phoneNameErrorClass}>
                    {resources.phoneNumberError}
                </div>
                <div className={this.state.storeNameErrorClass}>
                    {resources.storeNameError}
                </div>
                
            </>
        );
    }

    private _handleChange = (name: string) => (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement> | React.ChangeEvent<HTMLSelectElement> ) => {
        const value = e.target.value;
        switch (name) {
            case 'firstName': {
                this.setState({ firstName: value, fNError: false, reqError: false, eAValError: false});
                break;
            }
            case 'lastName': {
                this.setState({ lastName: value, lNError: false, reqError: false, eAValError: false });
                break;
            }
            case 'emailAddress': {
                this.setState({ emailAddress: value, eAError: false, reqError: false, eAValError: false });
                break;
            }
            case 'phoneNumber': {
                this.setState({ phoneNumber: value, pNError: false, reqError: false, eAValError: false });
                break;
            }
            case 'storeName': {
                this.setState({ storeName: value, sNError: false, reqError: false, eAValError: false });
                break;
            }
            default: {
                console.log('No input box changes');
                this.setState({ reqError: false, eAValError: false });
            }
        }
    }

    private _isEmailValid = (email: string): boolean => {
        const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return regex.test(email);
    };

    private _handleSubmit(e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | React.FormEvent<HTMLFormElement>) {
        this._recaptchaRef.current?.execute()
    }

    private async _submit(): Promise<void> {
        this._showErrors();
        
        this.firstNameValue = (this._FirstNameInputRef && this._FirstNameInputRef.current && this._FirstNameInputRef.current.value) ? true : false;
        this.lastNameValue = (this._LastNameInputRef && this._LastNameInputRef.current && this._LastNameInputRef.current.value) ? true : false;
        this.emailAddressValue = (this._EmailAddressInputRef && this._EmailAddressInputRef.current && this._EmailAddressInputRef.current.value) ? true : false;        
        this.phoneNumberValue = (this._PhoneInputRef && this._PhoneInputRef.current && this._PhoneInputRef.current.value) ? true : false;
        this.storeNameValue = (this._StoreInputRef && this._StoreInputRef.current && this._StoreInputRef.current.value) ? true : false;

        const fN = (this._FirstNameInputRef && this._FirstNameInputRef.current && this._FirstNameInputRef.current.value) ? this._FirstNameInputRef.current.value : '';
        const lN = (this._LastNameInputRef && this._LastNameInputRef.current && this._LastNameInputRef.current.value) ? this._LastNameInputRef.current.value : '';
        const eA = (this._EmailAddressInputRef && this._EmailAddressInputRef.current && this._EmailAddressInputRef.current.value) ? this._EmailAddressInputRef.current.value : '';
        const eAVal = this._EmailAddressInputRef.current!.value;
        const pN = (this._PhoneInputRef && this._PhoneInputRef.current && this._PhoneInputRef.current.value) ? this._PhoneInputRef.current.value : '';
        const sN = (this._StoreInputRef && this._StoreInputRef.current && this._StoreInputRef.current.value) ? this._StoreInputRef.current.value : '';

        const recaptchaValue = this._recaptchaRef.current?.getValue();

        if (!this.firstNameValue) {
            this.setState({
                fNError: true,
                reqError: true
            });
            this._recaptchaRef.current?.reset();
        } else if (!this.lastNameValue) {
            this.setState({
                lNError: true,
                reqError: true
            });
            this._recaptchaRef.current?.reset();
        } else if (!this.phoneNumberValue) {
            this.setState({
                pNError: true,
                reqError: true
            });
            this._recaptchaRef.current?.reset();
        } else if (!this.storeNameValue) {
            this.setState({
                sNError: true,
                reqError: true
            });
            this._recaptchaRef.current?.reset();
        } else if (!this.emailAddressValue) {
            this.setState({
                eAError: true,
                reqError: true
            });
            this._recaptchaRef.current?.reset();
        } else if (!this._isEmailValid(eAVal)) {
            this.setState({
                eAValError: true
            });
            this._recaptchaRef.current?.reset();
        } else if (!recaptchaValue) {
            this.setState({
                rCCError: true
            });
            this._recaptchaRef.current?.reset();
        } else {
            this.formInputValues = (this.emailAddressValue) ? true : false;
            if (this.formInputValues && !this.state.eAValError) {
                try {
                    await this._postEntry(fN, lN, eA, pN, sN);

                    this._clearValues();
                    this._recaptchaRef.current?.reset();
                    
                } catch (e) {
                    console.log(e, 'Please fill in the form!');
                }
            }
        }
    }

    private async _postEntry(
        firstName: string, 
        lastName: string, 
        email: string,
        phoneNumber: string,
        storeName: string
    ): Promise<void> {
        let emarsysGroupId = this.props.config.emarsysGroupId, 
        emarsysStoreNameGroupId = this.props.config.emarsysStoreNameGroupId;

        const response = await MsDyn365.commerceApiRequest(
            this.props.context.request,
            'PostEmarsysBlocContactEvent',
            'post',
            {
                firstName: firstName,
                lastName: lastName,
                emailAddress: email,
                phoneNumber: phoneNumber,
                country: 'NZ',
                emarsysGroupId: emarsysGroupId,
                storeName: storeName,
                storeNameField: emarsysStoreNameGroupId,
            }
        ) as any;
        
        this.responseMessage = response?.data.Message;

        if (response?.data.Result !== 'Error') {
            this.setState({
                responseReceived: true,
                responseError: response?.data.Result === 'Error',
                responseSuccess: response?.data.Result !== 'Error',
                showResponseSuccess: true,
                emailAddress: '',
                firstName: '',
                lastName: '',
                reqError: false,
                eAValError: false,
                rCCError: false
            });

            setTimeout(() => {  
                window.location.reload();
            }, 6000);

        } else {
            this.setState({
                responseReceived: true,
                responseError: response?.data.Result === 'Error',
                responseSuccess: response?.data.Result !== 'Error',
                emailAddress: '',
                reqError: false,
                eAValError: false,
                rCCError: false
            });
        }
    }

    private _clearValues(): void {
        if (this._EmailAddressInputRef.current) {
            this._EmailAddressInputRef.current!.value = '';
        }
        if (this._FirstNameInputRef.current) {
            this._FirstNameInputRef.current!.value = '';
        }
        if (this._LastNameInputRef.current) {
            this._LastNameInputRef.current!.value = '';
        }
        if (this._PhoneInputRef.current) {
            this._PhoneInputRef.current!.value = '';
        }
        if (this._StoreInputRef.current) {
            this._StoreInputRef.current!.value = '';
        }
    }

}

export default CittaBlocForm;


